"use client";

import { useState, useEffect, useRef } from "react";
import {
  ChevronRight,
  Check,
  Copy,
  X,
  Sparkles,
  Info,
  ChevronLeft,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  getCountriesListApi,
  getIndustriesListApi,
  saveOrgInfoApi,
  getDepartmentsMasterListApi,
  getOrgInfoApi,
  generateCompanyInfoStream,
  getOrgSuggestedDepartments,
  getOrgSuggestedJobRoles,
  generateCompanyJobRolesApi,
} from "../../services/settingsService";
import { getPeopleInviteLink } from "../../services/peopleService";
import { handleApiError } from "../shared/util";
import CustomTooltip from "./CustomTooltip";
import "./OnboardingWizardModal.css";

const OTHER_INDUSTRY_OPTION = "Other";

const isOtherIndustryOption = (value = "") =>
  value.trim().toLowerCase() === OTHER_INDUSTRY_OPTION.toLowerCase();

const OnboardingWizardModal = ({ isOpen, onComplete, onClose }) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false); // general
  const [isJobRolesLoading, setIsJobRolesLoading] = useState(false); // step 3 only
  const [isLoadingOrgInfo, setIsLoadingOrgInfo] = useState(false);
  const streamControllerRef = useRef(null);
  const streamRunIdRef = useRef(0);
  const [isSavingSelections, setIsSavingSelections] = useState(false); // final save

  const [canSkipStep, setCanSkipStep] = useState(false);
  const [departmentIsSelected, setDepartmentIsSelected] = useState(false);
  const [jobRoleIsSelected, setJobRoleIsSelected] = useState(false);

  // Step 1 - Organization Info
  const [orgInfo, setOrgInfo] = useState({
    org_name: "",
    industry: "",
    website: "",
    country: "",
    description: "",
  });
  const [savedIndustry, setSavedIndustry] = useState("");
  const [showCustomIndustryInput, setShowCustomIndustryInput] = useState(false);
  const [upskillCourseType, setUpskillCourseType] = useState("free");

  const [industries, setIndustries] = useState([]);
  const [countries, setCountries] = useState([]);
  const [orgErrors, setOrgErrors] = useState({});
  const [fetchedDescription, setFetchedDescription] = useState("");

  // Step 2 - Departments
  const [departments, setDepartments] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);

  // Step 3 - Job Roles + Invite
  const [jobRoles, setJobRoles] = useState([]);
  const [selectedJobRoles, setSelectedJobRoles] = useState([]);
  const [departmentSuggestions, setDepartmentSuggestions] = useState([]);

  // Step 3 - Invite Employees (moved from step 4)
  const [inviteLink, setInviteLink] = useState("");
  const [copySuccess, setCopySuccess] = useState(false);
  const [isInviteLinkLoading, setIsInviteLinkLoading] = useState(false);

  // Animation states
  const [stepAnimation, setStepAnimation] = useState("");
  const [completedSteps, setCompletedSteps] = useState([]);

  const [generatedJobRoles, setGeneratedJobRoles] = useState([]);

  useEffect(() => {
    if (selectedDepartments.length > 0) setDepartmentIsSelected(false);
  }, [selectedDepartments]);

  useEffect(() => {
    if (selectedJobRoles.length > 0) setJobRoleIsSelected(false);
  }, [selectedJobRoles]);

  useEffect(() => {
    if (isOpen) {
      setShowCustomIndustryInput(false);
      setSavedIndustry("");
      loadInitialData();
      setCurrentStep(1);
      setCompletedSteps([]);
      setStepAnimation("onboarding-wizard-fade-in");
    }
  }, [isOpen]);

  useEffect(() => {
    setCanSkipStep(currentStep > 1);
  }, [currentStep]);

  const loadInitialData = async () => {
    try {
      setIsLoading(true);
      await Promise.all([
        getIndustriesListApiHandler(),
        getCountriesListApiHandler(),
        getDepartmentsMasterListApiHandler(),
        fetchExistingOrgInfo(),
      ]);
    } catch (error) {
      console.error("Error loading initial data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchExistingOrgInfo = async () => {
    try {
      const response = await getOrgInfoApi();
      if (response.code === 200 && response.data) {
        const fetchedIndustry = response.data.industry || "";

        setOrgInfo((prev) => ({
          ...prev,
          org_name: response.data.organization || "",
          industry: fetchedIndustry,
          website: response.data.website || "",
          country: response.data.country || "",
          description: "",
        }));
        setSavedIndustry(fetchedIndustry);
        setShowCustomIndustryInput(false);
        if (response.data.upskillCourseType) {
          setUpskillCourseType(response.data.upskillCourseType);
        }
        setFetchedDescription(response.data.description || "");
      }
    } catch (error) {
      console.log("No existing organization data found");
    }
  };

  const getIndustriesListApiHandler = async () => {
    try {
      const response = await getIndustriesListApi();
      setIndustries(response.data || []);
    } catch (error) {
      handleApiError(error, { showModal: false });
    }
  };

  const getCountriesListApiHandler = async () => {
    try {
      const response = await getCountriesListApi();
      setCountries(response.data || []);
    } catch (error) {
      handleApiError(error, { showModal: false });
    }
  };

  const getDepartmentsListApiHandler = async () => {
    try {
      const response = await getOrgSuggestedDepartments();
      setDepartments(response.data || []);
      console.log("Suggested Departments:", response.data);
    } catch (error) {
      handleApiError(error, { showModal: false });
    }
  };

  const getDepartmentsMasterListApiHandler = async () => {
    try {
      const response = await getDepartmentsMasterListApi();
      setDepartmentSuggestions(response.data || []);
    } catch (error) {
      handleApiError(error, { showModal: false });
    }
  };

  const generateCompanyJobRolesApiHandler = async () => {
    const obj = { departments: selectedDepartments, roles: selectedJobRoles };
    const response = await generateCompanyJobRolesApi(obj);
    setGeneratedJobRoles(response.data || []);
    return response; // so callers can await/handle it
  };

  const loadJobRoles = async () => {
    try {
      setIsJobRolesLoading(true);
      const response = await getOrgSuggestedJobRoles();
      setJobRoles(response.data || []);
      setDepartmentIsSelected(false);
    } catch (error) {
      handleApiError(error, { showModal: false });
    } finally {
      setIsJobRolesLoading(false);
    }
  };

  // When entering step 3, load job roles + invite link
  useEffect(() => {
    if (currentStep === 3) {
      if (jobRoles.length === 0) {
        loadJobRoles();
      }
      if (!inviteLink) {
        getInviteLinkHandler();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  const fetchOrganizationDescription = async () => {
    setIsLoadingOrgInfo(true);

    setOrgInfo((prev) => ({ ...prev, description: "" }));

    if (streamControllerRef.current) {
      try {
        streamControllerRef.current.abort();
      } catch {}
    }

    const controller = new AbortController();
    streamControllerRef.current = controller;
    const myRunId = ++streamRunIdRef.current;

    try {
      const payload = {
        url: orgInfo.website,
        name: orgInfo.org_name,
      };

      await generateCompanyInfoStream(
        payload,
        (chunk) => {
          if (myRunId !== streamRunIdRef.current) return;
          if (!chunk) return;

          setOrgInfo((prev) => ({
            ...prev,
            description: (prev.description || "") + String(chunk),
          }));
        },
        {
          signal: controller.signal,
          delayMs: 0,
          jsonField: null,
        },
      );
    } catch (error) {
      if (error?.name !== "AbortError") {
        handleApiError(error, { showModal: true });
      }
    } finally {
      if (myRunId === streamRunIdRef.current) {
        streamControllerRef.current = null;
        setIsLoadingOrgInfo(false);
      }
    }
  };

  const validateStep1 = () => {
    const errors = {};
    if (!orgInfo.org_name.trim())
      errors.org_name = t("organizationInfo.errors.orgNameRequired");
    if (!orgInfo.industry.trim())
      errors.industry = t("organizationInfo.errors.industryRequired");
    if (!orgInfo.website.trim())
      errors.website = t("organizationInfo.errors.websiteRequired");
    if (!orgInfo.country.trim())
      errors.country = t("organizationInfo.errors.countryRequired");

    setOrgErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleStep1Submit = async () => {
    if (!validateStep1()) return;

    try {
      setIsLoading(true);
      const payload = {
        orgName: orgInfo.org_name,
        description: orgInfo.description,
        industry: orgInfo.industry,
        website: orgInfo.website,
        country: orgInfo.country,
        upskillCourseType: upskillCourseType,
      };

      const response = await saveOrgInfoApi(payload);
      if (response.code === 200) {
        await getDepartmentsListApiHandler();
        setCompletedSteps([...completedSteps, 1]);
        handleNextStep();
      }
    } catch (error) {
      handleApiError(error, { showModal: true });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDepartmentSelection = (department) => {
    const isSelected = selectedDepartments.some(
      (selected) => selected === department,
    );

    if (isSelected) {
      setSelectedDepartments(
        selectedDepartments.filter((selected) => selected !== department),
      );
    } else {
      setSelectedDepartments([...selectedDepartments, department]);
    }
  };

  const handleJobRoleSelection = (jobRole) => {
    const isSelected = selectedJobRoles.some(
      (selected) => selected === jobRole,
    );

    if (isSelected) {
      setSelectedJobRoles(
        selectedJobRoles.filter((selected) => selected !== jobRole),
      );
    } else {
      setSelectedJobRoles([...selectedJobRoles, jobRole]);
    }
  };

  // 1 -> 2, 2 -> 3 (no step 4 anymore)
  const handleNextStep = () => {
    if (currentStep === 2 && selectedDepartments.length === 0) {
      setDepartmentIsSelected(true);
      return;
    }

    const nextStep = Math.min(currentStep + 1, 3);

    setStepAnimation("onboarding-wizard-slide-out-left");
    setTimeout(() => {
      setCurrentStep(nextStep);
      setStepAnimation("onboarding-wizard-slide-in-right");
    }, 300);
  };

  const handlePrevStep = () => {
    setStepAnimation("onboarding-wizard-slide-out-right");
    setTimeout(() => {
      setCurrentStep((prevStep) => Math.max(prevStep - 1, 1));
      setStepAnimation("onboarding-wizard-slide-in-left");
    }, 300);
  };

  const handleSkipStep = () => {
    if (canSkipStep) {
      if (currentStep === 2) {
        setCompletedSteps([...completedSteps, 2]);
      }
      handleNextStep();
    }
  };

  // Step 3 final completion (save job roles + finish)
  const handleComplete = async () => {
    if (selectedJobRoles.length === 0) {
      setJobRoleIsSelected(true);
      return;
    }

    try {
      setIsSavingSelections(true);
      await generateCompanyJobRolesApiHandler();
    } catch (error) {
      handleApiError(error, { showModal: true });
      return;
    } finally {
      setIsSavingSelections(false);
    }

    console.log("Onboarding complete:");
    setStepAnimation("onboarding-wizard-fade-out");
    sessionStorage.setItem("isSetupCompleted", true);
    setTimeout(() => {
      onComplete({
        organizationInfo: orgInfo,
        selectedDepartments,
        selectedJobRoles,
        upskillCourseType,
        completedSteps: [...completedSteps, currentStep],
      });
    }, 300);
  };

  const dialogClose = () => {
    console.log("Dialog closed");
    setCurrentStep(1);
    setCompletedSteps([]);
    onClose();
  };

  const handleInputChange = (field, value) => {
    setOrgInfo((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (orgErrors[field]) {
      setOrgErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleIndustryChange = (value) => {
    handleInputChange("industry", value);
  };

  const handleShowIndustryDropdown = () => {
    setShowCustomIndustryInput(false);
    setOrgInfo((prev) => ({
      ...prev,
      industry: savedIndustry,
    }));

    if (savedIndustry && orgErrors.industry) {
      setOrgErrors((prev) => ({
        ...prev,
        industry: "",
      }));
    }
  };

  const industryValue = orgInfo.industry || "";
  const industryOptions = Array.isArray(industries) ? industries : [];
  const shouldShowSavedIndustryOption =
    !showCustomIndustryInput &&
    industryValue &&
    !industryOptions.includes(industryValue) &&
    !isOtherIndustryOption(industryValue);

  const getStepTitle = () => {
    switch (currentStep) {
      case 1:
        return t("onboarding.organizationInformation");
      case 2:
        return t("organizationInfo.selectDepartments");
      case 3:
        return t("jobRoles.selectJobRolesAndInvite");
      default:
        return "";
    }
  };

  const areAllDepartmentsSelected =
    departments.length > 0 && selectedDepartments.length === departments.length;

  const toggleSelectAllDepartments = () => {
    if (areAllDepartmentsSelected) {
      setSelectedDepartments([]);
    } else {
      setSelectedDepartments(departments);
    }
  };

  const areAllJobRolesSelected =
    jobRoles.length > 0 && selectedJobRoles.length === jobRoles.length;

  const toggleSelectAllJobRoles = () => {
    if (areAllJobRolesSelected) {
      setSelectedJobRoles([]);
    } else {
      setSelectedJobRoles(jobRoles);
    }
  };

  const getInviteLinkHandler = async () => {
    try {
      setIsInviteLinkLoading(true);
      const response = await getPeopleInviteLink();
      if (response.code === 200) {
        console.log("Invite link:", response.data);
        setInviteLink(response.data.inviteLink);
      }
    } catch (error) {
      console.error("Error fetching invite link:", error);
      handleApiError(error, {
        showModal: false,
      });
    } finally {
      setIsInviteLinkLoading(false);
    }
  };

  const handleCopyInviteLink = () => {
    if (inviteLink) {
      window.navigator.clipboard.writeText(inviteLink);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="onboarding-wizard-overlay organization-setup-modal">
      <div className="onboarding-wizard-modal organization-setup-modal__content">
        {/* Header */}
        <div className="onboarding-wizard-header">
          <div className="onboarding-wizard-header-content">
            <h1 style={{ display: "inline-flex", alignItems: "center" }}>
              {getStepTitle()}
              {currentStep === 2 && (
                <CustomTooltip
                  tooltip={{
                    title: t(
                      "organizationInfo.tooltips.selectDepartmentsTitle",
                    ),
                    message: t(
                      "organizationInfo.tooltips.selectDepartmentsMessage",
                    ),
                  }}
                  id="onboarding-departments-info"
                  className="custom-tooltip-bottom"
                >
                  <Info
                    size={16}
                    color="white"
                    style={{
                      marginLeft: "8px",
                      marginTop: "8px",
                      cursor: "pointer",
                    }}
                  />
                </CustomTooltip>
              )}
              {currentStep === 3 && (
                <CustomTooltip
                  tooltip={{
                    title: t("organizationInfo.tooltips.selectJobRolesTitle"),
                    message: t(
                      "organizationInfo.tooltips.selectJobRolesMessage",
                    ),
                  }}
                  id="onboarding-jobroles-info"
                  className="custom-tooltip-bottom"
                >
                  <Info
                    size={16}
                    color="white"
                    style={{
                      marginLeft: "8px",
                      marginTop: "8px",
                      cursor: "pointer",
                    }}
                  />
                </CustomTooltip>
              )}
            </h1>
          </div>

          {/* Progress Bar (3 steps now) */}
          <div className="onboarding-wizard-progress-bar">
            <div
              className="onboarding-wizard-progress-fill"
              style={{ width: `${(currentStep / 3) * 100}%` }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="onboarding-wizard-content">
          <div className={`onboarding-wizard-step-content ${stepAnimation}`}>
            {/* Step 1: Organization Info */}
            {currentStep === 1 && (
              <div className="onboarding-wizard-org-info">
                <form className="onboarding-wizard-form onboarding-wizard-compact-form">
                  <div className="onboarding-wizard-form-row">
                    <div className="onboarding-wizard-form-group">
                      <div className="onboarding-wizard-label-info-row">
                        <label>
                          {t("organizationInfo.organizationName")}
                          <span className="onboarding-wizard-required">*</span>
                        </label>

                        <div className="onboarding-wizard-info-wrap">
                          <button
                            type="button"
                            className="onboarding-wizard-info-btn"
                            aria-label={t(
                              "onboarding.organizationNameNoteAriaLabel",
                            )}
                          >
                            <Info size={14} />
                          </button>

                          <div className="onboarding-wizard-info-popover onboarding-wizard-org-name-info-popover">
                            {t("onboarding.organizationNameLockedNote")}{" "}
                          </div>
                        </div>
                      </div>
                      <input
                        type="text"
                        value={orgInfo.org_name}
                        onChange={(e) =>
                          handleInputChange("org_name", e.target.value)
                        }
                        placeholder={t("onboarding.enterOrgName")}
                        className={orgErrors.org_name ? "error" : ""}
                      />
                      {orgErrors.org_name && (
                        <span className="onboarding-wizard-error">
                          {orgErrors.org_name}
                        </span>
                      )}
                    </div>

                    <div className="onboarding-wizard-form-group">
                      <label>
                        {t("organizationInfo.industry")}
                        <span className="onboarding-wizard-required">*</span>
                      </label>
                      {showCustomIndustryInput ? (
                        <div className="onboarding-wizard-industry-input-wrapper">
                          <input
                            type="text"
                            value={industryValue}
                            onChange={(e) =>
                              handleInputChange("industry", e.target.value)
                            }
                            placeholder={t("organizationInfo.enterIndustry")}
                            className={orgErrors.industry ? "error" : ""}
                          />
                          <button
                            type="button"
                            className="onboarding-wizard-industry-reset-btn"
                            onClick={handleShowIndustryDropdown}
                            aria-label={t(
                              "organizationInfo.showIndustryDropdown",
                            )}
                            title={t("organizationInfo.showIndustryDropdown")}
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ) : (
                        <select
                          value={industryValue}
                          onChange={(e) => handleIndustryChange(e.target.value)}
                          className={orgErrors.industry ? "error" : ""}
                        >
                          <option value="">
                            {t("onboarding.selectIndustry")}
                          </option>
                          {shouldShowSavedIndustryOption && (
                            <option value={industryValue}>
                              {industryValue}
                            </option>
                          )}
                          {industryOptions.map((industry, i) => (
                            <option key={i} value={industry}>
                              {industry}
                            </option>
                          ))}
                        </select>
                      )}
                      {orgErrors.industry && (
                        <span className="onboarding-wizard-error">
                          {orgErrors.industry}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="onboarding-wizard-form-row">
                    <div className="onboarding-wizard-form-group">
                      <label>
                        {t("organizationInfo.webAddress")}
                        <span className="onboarding-wizard-required">*</span>
                      </label>
                      <input
                        type="url"
                        value={orgInfo.website}
                        onChange={(e) =>
                          handleInputChange("website", e.target.value)
                        }
                        placeholder={t("organizationInfo.websitePlaceholder")}
                        className={orgErrors.website ? "error" : ""}
                      />
                      {orgErrors.website && (
                        <span className="onboarding-wizard-error">
                          {orgErrors.website}
                        </span>
                      )}
                    </div>

                    <div className="onboarding-wizard-form-group">
                      <label>
                        {t("organizationInfo.country")}
                        <span className="onboarding-wizard-required">*</span>
                      </label>
                      <select
                        value={orgInfo.country}
                        onChange={(e) =>
                          handleInputChange("country", e.target.value)
                        }
                        className={orgErrors.country ? "error" : ""}
                      >
                        <option value="">
                          {t("onboarding.selectCountry")}
                        </option>
                        {countries.map((country, i) => (
                          <option key={i} value={country}>
                            {country}
                          </option>
                        ))}
                      </select>
                      {orgErrors.country && (
                        <span className="onboarding-wizard-error">
                          {orgErrors.country}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="onboarding-wizard-form-group onboarding-wizard-full-width">
                    <div className="onboarding-wizard-description-header">
                      <label>
                        {t("organizationInfo.aboutYourOrganization")}
                        <span className="onboarding-wizard-required">*</span>
                      </label>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={fetchOrganizationDescription}
                        disabled={isLoadingOrgInfo}
                      >
                        {isLoadingOrgInfo ? (
                          <>
                            <div className="onboarding-wizard-btn-spinner"></div>
                            {t("onboarding.fetching")}
                          </>
                        ) : (
                          <>
                            {t("onboarding.fetchDescription")}
                            <Sparkles size={16} />
                          </>
                        )}
                      </button>
                    </div>
                    <textarea
                      value={orgInfo.description}
                      onChange={(e) =>
                        handleInputChange("description", e.target.value)
                      }
                      placeholder={t("organizationInfo.describeOrganization")}
                      rows={3}
                    />
                  </div>

                  <div className="onboarding-wizard-form-group onboarding-wizard-full-width">
                    <label style={{ marginTop: "20px" }}>
                      {t("organizationInfo.professionalDevelopmentModel")}
                    </label>
                    <div className="upskilling-toggle-container-wizard">
                      <button
                        type="button"
                        className={`upskilling-toggle-option-wizard ${
                          upskillCourseType === "free" ? "active" : ""
                        }`}
                        onClick={() => setUpskillCourseType("free")}
                      >
                        <span className="upskilling-option-label-wizard">
                          {t("organizationInfo.freeCourses")}
                        </span>
                        <span className="upskilling-option-desc-wizard">
                          {t("organizationInfo.freeCoursesDescription")}
                        </span>
                      </button>
                      <button
                        type="button"
                        className={`upskilling-toggle-option-wizard ${
                          upskillCourseType === "paid" ? "active" : ""
                        }`}
                        onClick={() => setUpskillCourseType("paid")}
                      >
                        <span className="upskilling-option-label-wizard">
                          {t("organizationInfo.premiumCourses")}
                        </span>
                        <span className="upskilling-option-desc-wizard">
                          {t("organizationInfo.premiumCoursesDescription")}
                        </span>
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            )}

            {/* Step 2: Departments */}
            {currentStep === 2 && (
              <div className="onboarding-wizard-job-roles">
                <div className="onboarding-wizard-bulkbar">
                  <span className="onboarding-wizard-bulkcount">
                    {t("onboarding.selectedCount", {
                      selected: selectedDepartments.length,
                      total: departments.length,
                    })}
                  </span>
                  <button
                    type="button"
                    className="onboarding-wizard-btn onboarding-wizard-btn-secondary onboarding-wizard-btn-sm"
                    onClick={toggleSelectAllDepartments}
                    disabled={!departments.length}
                  >
                    {areAllDepartmentsSelected
                      ? t("common.clearAll")
                      : t("common.selectAll")}
                  </button>
                </div>

                <div className="onboarding-wizard-departments-grid">
                  {departments.map((department, index) => {
                    const isSelected = selectedDepartments.some(
                      (selected) => selected === department,
                    );
                    return (
                      <div
                        key={index}
                        className={`onboarding-wizard-department-card ${
                          isSelected ? "selected" : ""
                        }`}
                        onClick={() => handleDepartmentSelection(department)}
                      >
                        <div className="onboarding-wizard-department-info">
                          <h3>{department}</h3>
                          <div className="onboarding-wizard-department-checkbox"></div>
                        </div>
                        {isSelected && (
                          <div className="onboarding-wizard-selected-indicator">
                            <Check size={16} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {selectedDepartments.length > 0 && (
                  <div className="onboarding-wizard-selection-summary">
                    <p>
                      {t("organizationInfo.selectedDepartmentsCount", {
                        count: selectedDepartments.length,
                      })}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Job Roles + Invite Employees */}
            {currentStep === 3 && (
              <div className="onboarding-wizard-job-roles">
                {isJobRolesLoading ? (
                  <div className="onboarding-wizard-loading">
                    <div className="onboarding-wizard-spinner"></div>
                    <p>{t("common.loading")}</p>
                  </div>
                ) : (
                  <>
                    {/* Job Roles */}
                    <div className="onboarding-wizard-bulkbar">
                      <span className="onboarding-wizard-bulkcount">
                        {t("onboarding.selectedCount", {
                          selected: selectedJobRoles.length,
                          total: jobRoles.length,
                        })}
                      </span>
                      <button
                        type="button"
                        className="onboarding-wizard-btn onboarding-wizard-btn-secondary onboarding-wizard-btn-sm"
                        onClick={toggleSelectAllJobRoles}
                        disabled={!jobRoles.length}
                      >
                        {areAllJobRolesSelected
                          ? t("common.clearAll")
                          : t("common.selectAll")}
                      </button>
                    </div>

                    <div className="onboarding-wizard-departments-grid">
                      {jobRoles.map((jobRole, index) => {
                        const isSelected = selectedJobRoles.some(
                          (selected) => selected === jobRole,
                        );
                        return (
                          <div
                            key={index}
                            className={`onboarding-wizard-job-role-card ${
                              isSelected ? "selected" : ""
                            }`}
                            onClick={() => handleJobRoleSelection(jobRole)}
                          >
                            <div className="onboarding-wizard-job-role-header">
                              <div className="onboarding-wizard-job-role-checkbox"></div>
                            </div>
                            <div className="onboarding-wizard-job-role-info">
                              <h3>{jobRole}</h3>
                            </div>
                            {isSelected && (
                              <div className="onboarding-wizard-selected-indicator">
                                <Check size={16} />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {selectedJobRoles.length > 0 && (
                      <div className="onboarding-wizard-selection-summary">
                        <p>
                          {t("jobRoles.selectedJobRoles", {
                            count: selectedJobRoles.length,
                          })}
                        </p>
                      </div>
                    )}

                    {/* Invite Employees (moved from step 4) */}
                    {/*<div className="onboarding-wizard-invite-employees">
                      <h2 className="onboarding-wizard-invite-title">
                        {t("onboarding.invitePeople")}
                      </h2>
                      <p className="onboarding-wizard-invite-description">
                        {t("onboarding.invitePeopleDescription")}
                      </p>

                      <div className="onboarding-wizard-invite-link-container">
                        <label className="onboarding-wizard-invite-link-label">
                          {t("onboarding.peopleInviteLink")}
                        </label>
                        <div className="onboarding-wizard-invite-link-input-wrapper">
                          <input
                            type="text"
                            value={inviteLink}
                            readOnly
                            className="onboarding-wizard-invite-link-input"
                            placeholder={
                              isInviteLinkLoading
                                ? t("onboarding.loadingInviteLink")
                                : t("onboarding.copyInvitePlaceholder")
                            }
                          />
                          <button
                            className="btn-primary onboarding-wizard-copy-btn"
                            onClick={handleCopyInviteLink}
                            disabled={!inviteLink || isInviteLinkLoading}
                          >
                            <Copy size={16} />
                            {copySuccess
                              ? t("onboarding.copied")
                              : t("onboarding.copy")}
                          </button>
                        </div>
                        {copySuccess && (
                          <div className="onboarding-wizard-invite-success-message show">
                            {t("onboarding.inviteLinkCopied")}
                          </div>
                        )}
                      </div>
                    </div>*/}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="onboarding-wizard-footer">
          <div
            className="onboarding-wizard-footer-actions"
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "end",
              alignItems: "center",
            }}
          >
            {currentStep > 1 && (
              <div className="onboarding-wizard-footer-left">
                <button
                  style={{ float: "right" }}
                  type="button"
                  className="onboarding-wizard-btn onboarding-wizard-btn-secondary"
                  onClick={handlePrevStep}
                  disabled={currentStep === 1 || isLoading}
                  aria-label={t("common.back")}
                >
                  <ChevronLeft size={16} />
                  {t("common.back")}
                </button>
              </div>
            )}

            <div className="onboarding-wizard-footer-right">
              {currentStep < 3 ? (
                <button
                  className="btn-primary onboarding-wizard-btn"
                  onClick={
                    currentStep === 1 ? handleStep1Submit : handleNextStep
                  }
                  disabled={isLoading || isSavingSelections}
                >
                  {isLoading || isSavingSelections ? (
                    <>
                      <div className="onboarding-wizard-btn-spinner"></div>
                      {t("common.saving")}
                    </>
                  ) : (
                    <>
                      {t("common.next")}
                      <ChevronRight size={16} />
                    </>
                  )}
                </button>
              ) : (
                <button
                  className="btn-primary onboarding-wizard-btn"
                  onClick={handleComplete}
                  disabled={isLoading || isSavingSelections}
                >
                  {isSavingSelections ? (
                    <>
                      <div className="onboarding-wizard-btn-spinner"></div>
                      {t("common.saving")}
                    </>
                  ) : (
                    <>
                      {t("onboarding.completeSetup")}
                      <Check size={16} />
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
          <div style={{ float: "right", fontSize: "12px", color: "red" }}>
            {departmentIsSelected && (
              <p>{t("onboarding.selectAtLeastOneDepartment")}</p>
            )}
          {jobRoleIsSelected && (
  <p>{t("onboarding.selectAtLeastOneRole")}</p>
)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizardModal;
